import { useMemo } from "react";
import {
  fmtCurrencyDec,
  fmtDate,
  fmtNumber,
  fmtDecimal,
  fmtPercent,
} from "../../utils/format";

// ---------- types ----------

interface ScheduleColumn {
  key: string;
  header: string;
  type: "text" | "number" | "currency" | "percent" | "date" | "year";
  width?: string;
}

interface ComputedScheduleTableProps {
  columns: ScheduleColumn[];
  rows: Record<string, unknown>[];
  highlightRow?: number;
  isComputing?: boolean;
}

// ---------- helpers ----------

function formatCell(value: unknown, type: ScheduleColumn["type"]): string {
  if (value == null) return "\u2014";
  switch (type) {
    case "currency":
      return fmtCurrencyDec(typeof value === "number" ? value : Number(value));
    case "year":
      return String(Math.round(Number(value)));
    case "number":
      return typeof value === "number"
        ? Number.isInteger(value)
          ? fmtNumber(value)
          : fmtDecimal(value)
        : String(value);
    case "percent":
      return fmtPercent(typeof value === "number" ? value : Number(value));
    case "date":
      return fmtDate(typeof value === "string" ? value : String(value));
    case "text":
    default:
      return String(value);
  }
}

function alignClass(type: ScheduleColumn["type"]): string {
  switch (type) {
    case "currency":
    case "number":
    case "percent":
    case "year":
      return "text-right";
    default:
      return "text-left";
  }
}

// ---------- component ----------

export default function ComputedScheduleTable({
  columns,
  rows,
  highlightRow,
  isComputing,
}: ComputedScheduleTableProps) {
  const columnWidths = useMemo(
    () =>
      columns.map((col) => col.width ?? (col.type === "text" ? "auto" : "100px")),
    [columns],
  );

  if (rows.length === 0) {
    return (
      <div className="bg-grove-50 border border-grove-300 rounded-lg p-6 text-center">
        <p className="text-sm text-bark-400">No schedule data available.</p>
      </div>
    );
  }

  return (
    <div
      className={`bg-grove-50 border border-grove-300 rounded-lg overflow-hidden ${
        isComputing ? "animate-pulse" : ""
      }`}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-grove-300">
              {columns.map((col, i) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider whitespace-nowrap ${alignClass(col.type)}`}
                  style={{ width: columnWidths[i] }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className={`border-b border-grove-200 last:border-0 transition-colors ${
                  rowIdx === highlightRow
                    ? "bg-honey-50"
                    : "hover:bg-grove-100/50"
                }`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-1.5 text-[13px] tabular-nums text-bark-700 whitespace-nowrap ${alignClass(col.type)}`}
                  >
                    {formatCell(row[col.key], col.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
